import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslations } from "../../../../../api/useTranslations";
import t from "../../../../../i18n";
import { toPath, useMatchPattern } from "../../../../../router/helpers/path";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import { Text } from "@ui/Text";
import { useDashboardLocks } from "../../hook/action/lock";
import { useDashboardEditorContext } from "../../hook/context/editor";

/**
 * @description This modal is quite unique.
 * It should be displayed when a super admin is about to take
 * over a dashboard by force unlocking it.
 */
export function ModalDashboardTakeOver({
  projectId,
  dashboardId,
}: {
  projectId: string;
  dashboardId: string;
}) {
  const translations = useTranslations();
  const navigate = useNavigate();
  const editor = useDashboardEditorContext();
  const { isLocked, isUserLocked } = useDashboardLocks(projectId);

  const leave = useCallback(() => {
    void editor?.clear();
    void navigate(
      toPath("/projects/:projectId/dashboards/:dashboardId", {
        projectId,
        dashboardId,
      }),
    );
    return true;
  }, [dashboardId, editor, navigate, projectId]);

  const [opened, openModal] = useState<boolean>(
    () => isLocked(dashboardId) && !isUserLocked(dashboardId),
  );
  const matchDashboardEdit = Boolean(
    useMatchPattern("/projects/:projectId/dashboards/:dashboardId/edit"),
  );

  useEffect(() => {
    if (opened) return;
    const lockedByAnotherUser = isLocked(dashboardId) && !isUserLocked(dashboardId);
    openModal(matchDashboardEdit && lockedByAnotherUser);
  }, [opened, matchDashboardEdit, isLocked, isUserLocked, dashboardId]);

  return (
    <Modal
      title={t("project.dashboard.transaction.admin_take_over.title", translations)}
      isOpen={opened}
      onRequestClose={() => true}
      closable={false}
    >
      <ModalForm
        onSubmit={leave}
        onRequestClose={() => true}
        cancellable={false}
        okButtonName={t("project.dashboard.transaction.admin_take_over.action")}
      >
        <Text type="body1">
          {t("project.dashboard.transaction.admin_take_over.text", translations)}
        </Text>
      </ModalForm>
    </Modal>
  );
}
